import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrashIcon, PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getAppSocket } from '@/lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';

type SupplyArea = {
  id: string;
  name: string;
  isActive?: number;
};

const SupplyAreas: React.FC = () => {
  const [areas, setAreas] = useState<SupplyArea[]>([]);
  const [newArea, setNewArea] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchAreas = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/settings/areas');
      setAreas(res.data?.areas || []);
    } catch (error: any) {
      console.error('Error fetching areas', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar las áreas');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const realtimeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/settings/areas') || path.startsWith('/api/users'))) {
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchAreas({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, []);

  const createArea = async () => {
    if (!newArea.trim()) {
      toast.error('Ingresa un nombre de área');
      return;
    }
    try {
      setSaving(true);
      await axios.post('/api/settings/areas', { name: newArea.trim() });
      toast.success('Área creada');
      setNewArea('');
      fetchAreas();
    } catch (error: any) {
      console.error('Error creating area', error);
      toast.error(error.response?.data?.error || 'No se pudo crear el área');
    } finally {
      setSaving(false);
    }
  };

  const deleteArea = async (id: string) => {
    if (!await confirmDialog('¿Eliminar esta área?')) return;
    try {
      setDeletingId(id);
      await axios.delete(`/api/settings/areas/${id}`);
      toast.success('Área eliminada');
      fetchAreas();
    } catch (error: any) {
      console.error('Error deleting area', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar el área');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Áreas de la empresa</h1>
            <p className="text-sm text-muted-foreground">Configura las áreas usadas en usuarios y pedidos internos.</p>
          </div>
          <Button variant="outline" onClick={() => fetchAreas()} disabled={loading}>
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Refrescar
          </Button>
        </div>

        <Card className="p-4 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-2">
            <Input
              placeholder="Nombre del área"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              disabled={saving}
              className="md:w-64"
            />
            <Button onClick={createArea} disabled={saving}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Crear área
            </Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {areas.map((area) => (
                  <TableRow key={area.id}>
                    <TableCell>{area.name}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteArea(area.id)}
                        disabled={deletingId === area.id}
                      >
                        <TrashIcon className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!areas.length && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-sm text-muted-foreground">
                      {loading ? 'Cargando áreas...' : 'No hay áreas configuradas.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SupplyAreas;


